import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import type {
  QuerySort,
  StructuredExpression as UiStructuredExpression,
} from "skedyul-ui/ui";
import type { StructuredFilter } from "skedyul-core/features/crm/resources/filterTypes";

// Local alias that matches UI's StructuredExpression for URL <-> filters conversion
export type StructuredExpression = UiStructuredExpression;

export const isPlainObject = (
  value: unknown,
): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export const isEmptyStructuredExpression = (
  expr: StructuredExpression | undefined | null,
): boolean => {
  if (!expr || (isPlainObject(expr) && Object.keys(expr).length === 0)) {
    return true;
  }

  if (isPlainObject(expr)) {
    const hasAND = Object.prototype.hasOwnProperty.call(expr, "AND");
    const hasOR = Object.prototype.hasOwnProperty.call(expr, "OR");
    const hasNOT = Object.prototype.hasOwnProperty.call(expr, "NOT");

    if (hasAND) {
      const arr = (expr as { AND?: StructuredFilter[] }).AND;
      if (!arr || arr.length === 0) return true;
      return arr.every((f) => isPlainObject(f) && Object.keys(f).length === 0);
    }
    if (hasOR) {
      const arr = (expr as { OR?: StructuredFilter[] }).OR;
      if (!arr || arr.length === 0) return true;
      return arr.every((f) => isPlainObject(f) && Object.keys(f).length === 0);
    }
    if (hasNOT) {
      const notExpr = (expr as { NOT?: StructuredExpression }).NOT;
      return isEmptyStructuredExpression(notExpr);
    }
  }

  return false;
};

export const searchParamsToStructuredFilters = (
  params: ReadonlyURLSearchParams,
): StructuredExpression => {
  const raw = params.get("filters");
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isPlainObject(parsed)) return parsed as StructuredExpression;
    return {};
  } catch {
    return {};
  }
};

export const buildSearchStringWithFilters = (
  existing: ReadonlyURLSearchParams,
  filters: StructuredExpression,
): string => {
  const params = new URLSearchParams(existing.toString());

  if (isEmptyStructuredExpression(filters)) {
    params.delete("filters");
  } else {
    params.set("filters", JSON.stringify(filters));
  }

  return params.toString();
};

export const applyFiltersToUrl = (
  router: AppRouterInstance,
  pathname: string,
  existing: ReadonlyURLSearchParams,
  filters: StructuredExpression,
): void => {
  const search = buildSearchStringWithFilters(existing, filters);
  const url = search ? `${pathname}?${search}` : pathname;
  router.push(url);
};

// Sort helpers (JSON-encoded like filters)
// New wire format expected by core: { key, direction } or an array of that
export type UrlSort = QuerySort;

export const searchParamsToSort = (
  params: ReadonlyURLSearchParams,
): UrlSort | undefined => {
  const raw = params.get("sort");
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    // Accept either new format or legacy and normalize to new format
    if (Array.isArray(parsed)) {
      const arr = parsed
        .map((p): { key: string; direction?: "asc" | "desc" } | null => {
          if (p && typeof p === "object") {
            const obj = p as Record<string, unknown>;
            if (typeof obj.key === "string") {
              return {
                key: obj.key,
                direction:
                  obj.direction === "asc" || obj.direction === "desc"
                    ? obj.direction
                    : undefined,
              };
            }
            if (
              typeof obj.field === "string" &&
              typeof obj.model === "string"
            ) {
              return {
                key: `${obj.model}.${obj.field}`,
                direction:
                  obj.direction === "asc" || obj.direction === "desc"
                    ? obj.direction
                    : undefined,
              };
            }
          }
          return null;
        })
        .filter(
          (v): v is { key: string; direction?: "asc" | "desc" } => v !== null,
        );
      return arr.length ? arr : undefined;
    }
    if (parsed && typeof parsed === "object") {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.key === "string") {
        return {
          key: obj.key,
          direction:
            obj.direction === "asc" || obj.direction === "desc"
              ? obj.direction
              : undefined,
        };
      }
      if (typeof obj.field === "string" && typeof obj.model === "string") {
        return {
          key: `${obj.model}.${obj.field}`,
          direction:
            obj.direction === "asc" || obj.direction === "desc"
              ? obj.direction
              : undefined,
        };
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
};

export const buildSearchStringWithSort = (
  existing: ReadonlyURLSearchParams,
  sort: UrlSort | undefined,
): string => {
  const params = new URLSearchParams(existing.toString());
  if (!sort) {
    params.delete("sort");
  } else {
    params.set("sort", JSON.stringify(sort));
  }
  return params.toString();
};

export const applySortToUrl = (
  router: AppRouterInstance,
  pathname: string,
  existing: ReadonlyURLSearchParams,
  sort: UrlSort | undefined,
): void => {
  const search = buildSearchStringWithSort(existing, sort);
  const url = search ? `${pathname}?${search}` : pathname;
  router.push(url);
};
